import {
  InputNumber,
  SelectPicker,
  toaster,
  Button,
  Toggle,
  Loader,
  InputGroup,
  Whisper,
  Input
} from "rsuite";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  EFFECT,
  EFFECT_OPTIONS,
  EFFECTS_BY_QUARTER,
  EVENTS,
  SCHEDULER_EVENTS,
  SIMPLE_EFFECTS
} from "../models/dimmer.model";
import DimmerApi from "../../../../../../services/Dimmer.api";
import NotificationFactory from "../../../../../../shared/Notifications";
import UpdateButton from "../../../../../shared/UpdateButton/UpdateButton";
import {delayTooltip, fadeTooltip} from "../utils/Dimmer.utils";
import {TfiSave} from "react-icons/tfi";
import {FaArrowDown, FaArrowRight} from "react-icons/fa";

const DimmerManualEffectsSection = ({
                                      deviceId,
                                      loading,
                                      channels,
                                      effects = null,
                                      onOpenEffects,
                                      onRefreshEffects,
                                      effectsAlwaysEnabled,
                                      onToggleEffectsAlwaysEnabled,
                                      deviceType,
                                      onChangeIsActiveEffects,
                                      effectDelay,
                                      onUpdateEffectDelay,
                                      effectFade,
                                      onUpdateEffectFade,
                                      schedulerMode,
                                      isCircularEffectsMode,
                                      onToggleCircularEffectsMode
                                    }) => {
  const [effectsState, setEffectsState] = useState({});
  const [repeatCounts, setRepeatCounts] = useState({});
  const [selectedEffectsForAll, setSelectedEffectsForAll] = useState({});
  const [selectedRepeatsForAll, setSelectedRepeatsForAll] = useState({});
  const [effectsLoading, setEffectsLoading] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [selectedSingleEffect, setSelectedSingleEffect] = useState(null);
  const [selected4ChEffect, setSelected4ChEffect] = useState(null);
  const [delay, setDelay] = useState(effectDelay);
  const [fade, setFade] = useState(effectFade);
  const [open, setOpen] = useState(false);

  const effectsByQuarter = useMemo(() => EFFECTS_BY_QUARTER, []);
  const simpleEffects = useMemo(() => SIMPLE_EFFECTS, []);
  const filteredEvents = useMemo(() => {
    const timeEvents = new Set([
      SCHEDULER_EVENTS.ENABLE_BY_TIME,
      SCHEDULER_EVENTS.DISABLE_BY_TIME,
    ]);
    const duskDawnEvents = new Set([
      SCHEDULER_EVENTS.DISABLE_BY_DAWN,
      SCHEDULER_EVENTS.ENABLE_BY_DUSK,
    ]);

    return EVENTS.filter((event) => {
      if (schedulerMode === "Timer") {
        return !duskDawnEvents.has(event.value);
      }

      if (schedulerMode === "DuskDawn") {
        return !timeEvents.has(event.value);
      }

      return !timeEvents.has(event.value) && !duskDawnEvents.has(event.value);
    });
  }, [schedulerMode]);

  useEffect(() => {
    if (channels.length > 0) {
      const initialEffects = {};
      const initialRepeats = {};
      const initialForAll = {};

      EVENTS.forEach(event => {
        initialEffects[event.value] = {};
        initialRepeats[event.value] = {};
        initialForAll[event.value] = {effect: EFFECT.None, repeat: 0};

        channels.forEach((_, index) => {
          initialEffects[event.value][index] = EFFECT.None;
          initialRepeats[event.value][index] = 0;
        });
      });

      if (effects && effects.length > 0) {
        effects.forEach(eventData => {
          const eventValue = eventData.event;
          const eventExists = EVENTS.some(e => e.value === eventValue);

          if (eventExists && eventData.channels) {
            const allEffectsSame = eventData.channels.every(
              (channel, index, arr) => index === 0 || channel.effect === arr[0].effect
            );

            const allRepeatsSame = eventData.channels.every(
              (channel, index, arr) => index === 0 || channel.repeatCount === arr[0].repeatCount
            );

            if (allEffectsSame && eventData.channels[0]) {
              initialForAll[eventValue].effect =
                eventData.channels[0].effect || EFFECT.None;
            }

            if (allRepeatsSame && eventData.channels[0]) {
              initialForAll[eventValue].repeat =
                eventData.channels[0].repeatCount || 0;
            }

            eventData.channels.forEach((channelData, index) => {
              if (index < channels.length) {
                initialEffects[eventValue][index] = channelData.effect || EFFECT.None;
                initialRepeats[eventValue][index] =
                  channelData.repeatCount || 0;
              }
            });
          }
        });
      }

      setEffectsState(initialEffects);
      setRepeatCounts(initialRepeats);

      const effectsForAll = {};
      const repeatsForAll = {};

      EVENTS.forEach(event => {
        effectsForAll[event.value] = initialForAll[event.value].effect;
        repeatsForAll[event.value] = initialForAll[event.value].repeat;
      });

      setSelectedEffectsForAll(effectsForAll);
      setSelectedRepeatsForAll(repeatsForAll);
    }
  }, [channels, effects]);

  const effectsEnabled = useCallback(() => {
    return filteredEvents.some(event =>
      channels.some((_, channelIndex) =>
        effectsState[event.value]?.[channelIndex] !== undefined &&
        effectsState[event.value][channelIndex] !== EFFECT.None
      )
    );
  }, [channels, effectsState, filteredEvents]);

  useEffect(() => {
    if (typeof onChangeIsActiveEffects === "function") {
      onChangeIsActiveEffects(effectsEnabled());
    }
  }, [effectsEnabled, onChangeIsActiveEffects]);

  useEffect(() => {
    setDelay(effectDelay);
  }, [effectDelay]);

  useEffect(() => {
    setFade(effectFade);
  }, [effectFade]);

  // все эффекты для события == None → блок серый
  const isEventDisabled = (eventValue) => {
    const eventEffects = effectsState[eventValue];
    if (!eventEffects) return true;

    return channels.every((_, index) => {
      const effect = eventEffects[index] ?? EFFECT.None;
      return effect === EFFECT.None;
    });
  };

  const disableAllEffects = async () => {
    setSavingAll(true);

    try {
      const promises = EVENTS.map(event =>
        DimmerApi.updateEffects(deviceId, {
          event: event.value,
          channels: channels.map(() => ({
            effect: EFFECT.None,
            repeatCount: 0
          }))
        })
      );

      await Promise.all(promises);

      const newEffectsState = {};
      const newRepeatCounts = {};
      const newEffectsForAll = {};
      const newRepeatsForAll = {};

      EVENTS.forEach(event => {
        newEffectsState[event.value] = {};
        newRepeatCounts[event.value] = {};
        newEffectsForAll[event.value] = EFFECT.None;
        newRepeatsForAll[event.value] = 0;

        channels.forEach((_, index) => {
          newEffectsState[event.value][index] = EFFECT.None;
          newRepeatCounts[event.value][index] = 0;
        });
      });

      setEffectsState(newEffectsState);
      setRepeatCounts(newRepeatCounts);
      setSelectedEffectsForAll(newEffectsForAll);
      setSelectedRepeatsForAll(newRepeatsForAll);

      toaster.push(
        NotificationFactory.success("Все эффекты выключены"),
        {placement: "topEnd"}
      );

      if (onRefreshEffects) {
        await onRefreshEffects();
      }
    } catch (error) {
      console.error("Ошибка при выключении эффектов:", error);
      toaster.push(
        NotificationFactory.error("Не удалось выключить эффекты"),
        {placement: "topEnd"}
      );
    } finally {
      setSavingAll(false);
    }
  };

  const handleEffectsToggle = async (checked) => {
    if (!checked) {
      await disableAllEffects();
    }

    if (typeof onChangeIsActiveEffects === "function") {
      onChangeIsActiveEffects(checked);
    }
  };

  const handleEffectChange = (eventValue, channelIndex, value) => {
    setEffectsState(prev => ({
      ...prev,
      [eventValue]: {
        ...prev[eventValue],
        [channelIndex]: value
      }
    }));

    if (value === EFFECT.None) {
      setRepeatCounts(prev => ({
        ...prev,
        [eventValue]: {
          ...prev[eventValue],
          [channelIndex]: 0
        }
      }));
    }
  };

  const handleRepeatChange = (eventValue, channelIndex, value) => {
    setRepeatCounts(prev => ({
      ...prev,
      [eventValue]: {
        ...prev[eventValue],
        [channelIndex]: value
      }
    }));
  };

  const handleEffectForAllChange = (eventValue, value) => {
    setSelectedEffectsForAll(prev => ({
      ...prev,
      [eventValue]: value
    }));

    if (value === EFFECT.None) {
      setSelectedRepeatsForAll(prev => ({
        ...prev,
        [eventValue]: 0
      }));
    }
  };

  const handleRepeatForAllChange = (eventValue, value) => {
    setSelectedRepeatsForAll(prev => ({
      ...prev,
      [eventValue]: value
    }));
  };

  const applyEffectToAllChannels = (eventValue) => {
    const selectedEffect = selectedEffectsForAll[eventValue];
    const selectedRepeat = selectedRepeatsForAll[eventValue];

    if (selectedEffect === undefined) return;

    const newEffectsState = {...effectsState};
    const newRepeatCounts = {...repeatCounts};

    channels.forEach((_, index) => {
      newEffectsState[eventValue] = {
        ...newEffectsState[eventValue],
        [index]: selectedEffect
      };

      newRepeatCounts[eventValue] = {
        ...newRepeatCounts[eventValue],
        [index]: selectedEffect === EFFECT.None ? 0 : selectedRepeat
      };
    });

    setEffectsState(newEffectsState);
    setRepeatCounts(newRepeatCounts);
  };

  const handleApplyEffects = async (eventValue) => {
    const event = EVENTS.find(e => e.value === eventValue);
    if (!event) return;

    setEffectsLoading(true);

    const payload = {
      event: eventValue,
      channels: channels.map((_, index) => {
        const effect = effectsState[eventValue]?.[index] || EFFECT.None;
        const repeatCount = +(repeatCounts[eventValue]?.[index] ?? 0);

        return {
          effect,
          repeatCount: effect === EFFECT.None ? 0 : repeatCount
        };
      })
    };

    try {
      await DimmerApi.updateEffects(deviceId, payload);
      toaster.push(
        NotificationFactory.success(
          `Эффекты для события "${event.label}" успешно применены`
        ),
        {placement: "topEnd"}
      );
      if (onRefreshEffects) {
        await onRefreshEffects();
      }
    } catch (e) {
      console.error("Ошибка:", e);
      toaster.push(
        NotificationFactory.error(
          `Ошибка при применении эффектов для события "${event.label}"`
        ),
        {placement: "topEnd"}
      );
    } finally {
      setEffectsLoading(false);
    }
  };

  const handleRunDimmerEffect4Ch = async (effect) => {
    if (!effect) return;
    setEffectsLoading(true);
    try {
      await DimmerApi.runTestFourChannelEffects(deviceId, effect);
      toaster.push(
        NotificationFactory.success("Тест 4-х каналов запущен"),
        {placement: "topEnd"}
      );
      if (onRefreshEffects) {
        await onRefreshEffects();
      }
    } catch (e) {
      console.error("Ошибка:", e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setEffectsLoading(false);
    }
  };

  const handleRunDimmerEffect = async (effect) => {
    if (!effect) return;
    setEffectsLoading(true);
    try {
      await DimmerApi.runTestAllChannelEffects(deviceId, effect);
      toaster.push(
        NotificationFactory.success("Тест всех каналов запущен"),
        {placement: "topEnd"}
      );
      if (onRefreshEffects) {
        await onRefreshEffects();
      }
    } catch (e) {
      console.error("Ошибка:", e);
      toaster.push(
        NotificationFactory.error("Ошибка"),
        {placement: "topEnd"}
      );
    } finally {
      setEffectsLoading(false);
    }
  };

  const handleSaveDelay = () => {
    onUpdateEffectDelay(delay);
  };

  const handleSaveFade = () => {
    onUpdateEffectFade(fade);
  };

  const toggleOpen = () => {
    setOpen((isOpen) => {
      const willOpen = !isOpen;
      if (willOpen) onOpenEffects?.();
      return willOpen;
    });
  };

  return (
    <>
      <div
        className="mb-2 mt-3 dimmer-effects-panel"
        style={{border: "1px solid #e5e5ea", borderRadius: 6}}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            background: "#f7f7fa",
            borderBottom: open ? "1px solid #e5e5ea" : "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
          onClick={toggleOpen}
        >
          <span>Эффекты по каналам</span>
          <span>{open ? <FaArrowDown/> : <FaArrowRight/>}</span>
        </div>

        {open && (
          <div style={{padding: "8px 12px"}}>

            {effects?.length ? (
              <div>
                <div className="row p-2">
                  <div className="col-md-3 text-center">
                    <Toggle
                      size="sm"
                      color="blue"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      checked={effectsEnabled()}
                      onChange={handleEffectsToggle}
                      loading={loading || savingAll}
                    >
                      Эффекты вкл/выкл
                    </Toggle>
                  </div>
                  <div className="col-md-3 text-center">
                    <Toggle
                      size="sm"
                      color="blue"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      checked={effectsAlwaysEnabled}
                      onChange={onToggleEffectsAlwaysEnabled}
                      loading={loading}
                    >
                      Эффекты в любое время
                    </Toggle>
                  </div>
                  <div className="col-md-3 text-center">
                    <Toggle
                      size="sm"
                      color="blue"
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                      checked={isCircularEffectsMode || false}
                      onChange={onToggleCircularEffectsMode}
                      loading={loading}
                    >
                      isCircularEffectsMode
                    </Toggle>
                  </div>
                  <div className="col-md-3 text-end">
                    <UpdateButton
                      onRefresh={onRefreshEffects}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="row p-2">
                  <div className="col-md-3 mt-2 mb-2">
                    <InputGroup>
                      <InputGroup.Addon>Повторы</InputGroup.Addon>
                      <Whisper
                        placement="top"
                        controlId="control-id-delay"
                        trigger="hover"
                        speaker={delayTooltip}
                      >
                        <Input
                          value={delay}
                          type="number"
                          min={0.1}
                          max={25.5}
                          step={0.1}
                          onChange={(value) => setDelay(value)}
                        />
                      </Whisper>
                      <InputGroup.Addon
                        style={{cursor: "pointer"}}
                        onClick={handleSaveDelay}
                      >
                        <TfiSave/>
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>

                  <div className="col-md-3 mt-2 mb-2">
                    <InputGroup>
                      <InputGroup.Addon>Плавность</InputGroup.Addon>
                      <Whisper
                        placement="top"
                        controlId="control-id-fade"
                        trigger="hover"
                        speaker={fadeTooltip}
                      >
                        <Input
                          value={fade}
                          type="number"
                          min={0}
                          max={255}
                          step={1}
                          onChange={(value) => setFade(value)}
                        />
                      </Whisper>
                      <InputGroup.Addon
                        style={{cursor: "pointer"}}
                        onClick={handleSaveFade}
                      >
                        <TfiSave/>
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                </div>

                <div className="row p-1 pt-0">
                  <div className="col-md-12">
                    <div className="mb-3 ps-2">
                      {filteredEvents.map(event => {
                        const disabledEvent = isEventDisabled(event.value);

                        return (
                          <div
                            key={event.value}
                            className="mb-4"
                            style={{
                              backgroundColor: disabledEvent ? "#f5f5f5" : '#dcf2fc',
                              borderRadius: 4,
                              padding: "4px 8px",
                            }}
                          >
                            <div className="row mt-3 mb-2">
                              <div className="col-md-4 d-flex align-items-center mb-2">
                                <h6
                                  className="me-2"
                                  style={{color: "#0A4270", fontSize: 14}}
                                >
                                  {event.label}
                                </h6>
                                <Button
                                  size="sm"
                                  appearance="primary"
                                  loading={loading || effectsLoading}
                                  disabled={loading || effectsLoading}
                                  onClick={() => handleApplyEffects(event.value)}
                                >
                                  Применить
                                </Button>
                              </div>

                              <div className="col-md-8 d-flex align-items-center">
                                <div className="d-flex align-items-center me-3">
                                  <SelectPicker
                                    size="xs"
                                    className="me-2"
                                    data={EFFECT_OPTIONS}
                                    value={
                                      selectedEffectsForAll[event.value] || EFFECT.None
                                    }
                                    onChange={(value) =>
                                      handleEffectForAllChange(event.value, value)
                                    }
                                    cleanable={false}
                                    searchable={false}
                                    placeholder="Выберите эффект"
                                    block
                                    disabled={
                                      loading || effectsLoading || savingAll
                                    }
                                  />
                                </div>

                                <div className="d-flex align-items-center me-3">
                                  <InputNumber
                                    size="xs"
                                    style={{width: 70}}
                                    value={
                                      selectedRepeatsForAll[event.value] || 0
                                    }
                                    onChange={(value) =>
                                      handleRepeatForAllChange(event.value, value)
                                    }
                                    min={0}
                                    max={255}
                                    disabled={
                                      loading ||
                                      effectsLoading ||
                                      savingAll ||
                                      selectedEffectsForAll[event.value] ===
                                      EFFECT.None
                                    }
                                    placeholder="Повторы"
                                  />
                                </div>

                                <Button
                                  size="xs"
                                  style={{width: 150}}
                                  appearance="ghost"
                                  onClick={() =>
                                    applyEffectToAllChannels(event.value)
                                  }
                                  loading={
                                    loading || effectsLoading || savingAll
                                  }
                                  disabled={
                                    loading || effectsLoading || savingAll
                                  }
                                >
                                  Выбрать для всех
                                </Button>
                              </div>
                            </div>

                            {/* Шапка каналов */}
                            <div className="row mb-2">
                              <div className="col-md-1">
                                <strong>Канал</strong>
                              </div>
                              {channels.map((channel, index) => (
                                <div
                                  style={{width: 140}}
                                  className="text-center"
                                  key={`header-${event.value}-${index}`}
                                >
                                  <strong>#{index + 1}</strong>
                                </div>
                              ))}
                            </div>

                            {/* Эффекты */}
                            <div className="row mb-3 align-items-center">
                              <div className="col-md-1">
                                <span>Эффект:</span>
                              </div>
                              {channels.map((channel, channelIndex) => (
                                <div
                                  style={{width: 140}}
                                  key={`effect-${event.value}-${channelIndex}`}
                                >
                                  <SelectPicker
                                    data={EFFECT_OPTIONS}
                                    value={
                                      effectsState[event.value]?.[channelIndex] ||
                                      EFFECT.None
                                    }
                                    onChange={(value) =>
                                      handleEffectChange(
                                        event.value,
                                        channelIndex,
                                        value
                                      )
                                    }
                                    cleanable={false}
                                    searchable={false}
                                    size="xs"
                                    block
                                    loading={
                                      loading || effectsLoading || savingAll
                                    }
                                    disabled={
                                      loading || effectsLoading || savingAll
                                    }
                                  />
                                </div>
                              ))}
                            </div>

                            {/* Повторы */}
                            <div className="row align-items-center mb-4">
                              <div className="col-md-1">
                                <span>Повторов:</span>
                              </div>
                              {channels.map((channel, channelIndex) => {
                                const effectValue =
                                  effectsState[event.value]?.[channelIndex] ||
                                  EFFECT.None;
                                return (
                                  <div
                                    style={{width: 140}}
                                    key={`repeat-${event.value}-${channelIndex}`}
                                  >
                                    <InputNumber
                                      value={
                                        repeatCounts[event.value]?.[
                                          channelIndex
                                          ] || 0
                                      }
                                      onChange={(value) =>
                                        handleRepeatChange(
                                          event.value,
                                          channelIndex,
                                          value
                                        )
                                      }
                                      min={0}
                                      max={255}
                                      disabled={
                                        effectValue === EFFECT.None ||
                                        loading ||
                                        effectsLoading ||
                                        savingAll
                                      }
                                      size="xs"
                                      style={{width: "100%"}}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {deviceType !== 83 && (
                  <div className="row p-2">
                    <div className="col-md-3">
                      <SelectPicker
                        style={{width: 200}}
                        cleanable={false}
                        searchable={false}
                        data={effectsByQuarter}
                        value={selected4ChEffect}
                        onChange={setSelected4ChEffect}
                        placeholder="Выберите эффект"
                      />
                    </div>
                    <div className="col-md-3">
                      <Button
                        style={{width: "100%"}}
                        appearance="ghost"
                        onClick={async () =>
                          await handleRunDimmerEffect4Ch(selected4ChEffect)
                        }
                        disabled={loading || selected4ChEffect == null}
                      >
                        Тест 4 каналов
                      </Button>
                    </div>
                  </div>
                )}

                <div className="row p-2">
                  <div className="col-md-3 mb-2">
                    <SelectPicker
                      style={{width: 200}}
                      cleanable={false}
                      searchable={false}
                      data={simpleEffects}
                      value={selectedSingleEffect}
                      onChange={setSelectedSingleEffect}
                      placeholder="Выберите эффект"
                    />
                  </div>
                  <div className="col-md-3">
                    <Button
                      appearance="ghost"
                      style={{width: "100%"}}
                      onClick={async () =>
                        await handleRunDimmerEffect(selectedSingleEffect)
                      }
                      disabled={loading || selectedSingleEffect == null}
                    >
                      Тест каждого канала
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Loader size="md"/>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DimmerManualEffectsSection;
